/**
 * The gateways over the last days (19/10/2026, step 4), one folded section:
 * a 7 / 14 / 30 day switch, then one row per gateway — days with a send,
 * drops, SMS, delivered %, bot % and CR over the range, the days a verdict
 * blocked it, and the delivered-% line with blocked days dotted. Answers
 * "is this gateway getting worse?" without opening any container.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import type { DropGenGatewayTrend, DropGenGatewayTrends } from '@trafficpulse/shared';

import { SegmentedPills } from '@/components/ui';

import { Collapse } from '../../timeline/section/Collapse';
import { SectionToggle } from '../../timeline/section/SectionToggle';
import { Sparkline } from './Sparkline';

const GRID = 'grid grid-cols-[minmax(180px,1.3fr)_64px_72px_90px_84px_72px_72px_80px_160px] items-center gap-3 px-5';
const DAYS = [
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
] as const;

const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(v * 100 >= 10 ? 0 : 1)}%`);
const n = (v: number) => v.toLocaleString('en-US');

const summary = (t: DropGenGatewayTrends): string => {
  const active = t.gateways.filter((g) => g.total.dropsSent > 0);
  const blocked = t.gateways.filter((g) => g.blockedDays > 0);
  const worst = active.filter((g) => g.total.deliveredRate !== null).sort((a, b) => (a.total.deliveredRate ?? 1) - (b.total.deliveredRate ?? 1))[0];
  return [
    `${active.length} gateway${active.length === 1 ? '' : 's'} sent in the last ${t.days} days`,
    blocked.length > 0 ? `${blocked.length} blocked on some day` : null,
    worst ? `lowest delivered ${pct(worst.total.deliveredRate)} (${worst.routeName ?? `#${worst.routeId}`})` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

const Row: FC<{ g: DropGenGatewayTrend; from: string; to: string }> = ({ g, from, to }) => (
  <div className={`${GRID} border-t border-border/30 py-1.5 text-xs`} data-testid="trend-row">
    <span className="truncate font-medium" title={g.routeName ?? undefined}>{g.routeName ?? `Gateway ${g.routeId}`}</span>
    <span className="text-right font-mono tabular-nums">{g.days.filter((d) => d.dropsSent > 0).length}</span>
    <span className="text-right font-mono tabular-nums">{g.total.dropsSent}</span>
    <span className="text-right font-mono tabular-nums">{n(g.total.smsSent)}</span>
    <span className="text-right font-mono tabular-nums">{pct(g.total.deliveredRate)}</span>
    <span className="text-right font-mono tabular-nums">{pct(g.total.botRate)}</span>
    <span className="text-right font-mono tabular-nums">{pct(g.total.cr)}</span>
    <span className={`text-right font-mono tabular-nums ${g.blockedDays > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground'}`}>{g.blockedDays > 0 ? g.blockedDays : '—'}</span>
    <span className="flex justify-end"><Sparkline days={g.days} from={from} to={to} /></span>
  </div>
);

export const TrendsSection: FC<{ trends: DropGenGatewayTrends | undefined; days: number; onDays: (days: number) => void; loading: boolean }> = ({ trends, days, onDays, loading }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border/40" data-testid="trends-section">
      <SectionToggle icon={TrendingUp} title="Last days" badge={`${days} d`} summary={trends ? summary(trends) : loading ? 'reading…' : ''} open={open} onToggle={() => setOpen((v) => !v)} />
      <Collapse open={open}>
        <div className="flex items-center justify-end px-5 pb-2">
          <SegmentedPills label="Range" options={DAYS} value={String(days) as '7' | '14' | '30'} onChange={(v) => onDays(Number(v))} />
        </div>
        {trends && trends.gateways.length > 0 ? (
          <div className="overflow-x-auto pb-2">
            <div className="min-w-[960px]">
              <div className={`${GRID} py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
                <span>Gateway</span><span className="text-right">Days</span><span className="text-right">Drops</span><span className="text-right">SMS</span><span className="text-right">Delivered</span><span className="text-right">Bot</span><span className="text-right">CR</span><span className="text-right">Blocked</span><span className="text-right">Delivered by day</span>
              </div>
              {trends.gateways.map((g) => <Row key={g.routeId} g={g} from={trends.from} to={trends.to} />)}
            </div>
          </div>
        ) : (
          <p className="px-5 pb-3 text-xs text-muted-foreground">{loading ? 'Reading the last days…' : `No send in the last ${days} days.`}</p>
        )}
        <p className="px-5 pb-3 text-[11px] text-muted-foreground">Rates over the whole range from the drops with stats. The line is the delivered % by day; a red dot marks a day a verdict blocked the gateway; the thin line is the 85 % limit.</p>
      </Collapse>
    </div>
  );
};
